import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { BackButton } from "@/components/BackButton";
import { api } from "@/lib/api";
import { StarField } from "@/components/alignment/StarField";
import { Sparkles, BookOpen, Wind, Moon, Brain, Sprout, Flame, Star, Lock } from "lucide-react";

const BADGE_ICON = { sparkles: Sparkles, book: BookOpen, wind: Wind, moon: Moon, brain: Brain, sprout: Sprout, flame: Flame, star: Star };

const STAT_LABELS = {
  lessons: "Lessons", reflections: "Reflections", calm_sessions: "Calm sessions",
  wisdom_answered: "Wisdom answered", karma_choices: "Garden choices", quizzes: "Quizzes",
  days_practiced: "Days practiced", total_practices: "Total practices",
};

export default function Journey() {
  const [j, setJ] = useState(null);
  useEffect(() => { api.get("/journey").then((r) => setJ(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen relative text-slate-200 overflow-hidden" data-testid="journey-page">
      <div className="absolute inset-0" style={{ background: "radial-gradient(1000px 600px at 15% -10%, rgba(120,90,20,0.35), transparent 60%), radial-gradient(900px 700px at 90% 0%, rgba(88,28,135,0.4), transparent 60%), linear-gradient(180deg,#070512,#0A0A14)" }} />
      <StarField color="#F5C842" />
      <div className="relative z-10">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <BackButton className="mb-6" />
          <p className="text-sm tracking-[0.3em] uppercase text-amber-300/80 mb-3">Your Journey</p>
          <h1 className="font-head font-900 text-4xl sm:text-5xl text-slate-50 mb-2">PROGRESS, NOT PERFECTION</h1>
          <p className="text-slate-400 max-w-xl mb-10 text-base">These numbers describe your practice and engagement — never your worth. Enlightenment is a path, not a score.</p>

          {j ? (
            <>
              <div className="rounded-2xl border border-[#F5C842]/25 bg-white/[0.03] backdrop-blur-md p-7 mb-8">
                <div className="flex items-end justify-between mb-3">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">Level</p>
                    <p className="font-head font-900 text-5xl text-slate-50" data-testid="journey-level">{j.level}</p>
                  </div>
                  <div className="text-right">
                    <p className="inline-flex items-center gap-2 text-lg text-[#F5C842]"><Flame className="w-5 h-5" /> {j.streak} day streak</p>
                    <p className="text-xs text-slate-400 mt-1" data-testid="journey-xp">{j.xp} / {j.next_level_xp} XP</p>
                  </div>
                </div>
                <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#A78BFA] via-[#2DD4BF] to-[#F5C842] transition-all duration-700" style={{ width: `${j.progress_pct}%` }} />
                </div>
              </div>

              <p className="text-sm tracking-[0.25em] uppercase text-slate-400 mb-4">Your practice</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
                {Object.entries(STAT_LABELS).map(([k, label]) => (
                  <div key={k} className="rounded-xl border border-white/10 p-4 bg-white/[0.02] text-center">
                    <p className="font-head font-900 text-2xl text-slate-50" data-testid={`journey-stat-${k}`}>{j.stats[k] ?? 0}</p>
                    <p className="text-xs text-slate-500 mt-1">{label}</p>
                  </div>
                ))}
              </div>

              <p className="text-sm tracking-[0.25em] uppercase text-slate-400 mb-4">Badges</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="journey-badges">
                {j.badges.map((b) => {
                  const Icon = BADGE_ICON[b.icon] || Sparkles;
                  return (
                    <div key={b.id} data-testid={`badge-${b.id}`} className={`rounded-2xl border p-5 text-center transition-all duration-300 ${b.earned ? "border-[#F5C842]/40 bg-[#F5C842]/5" : "border-white/10 bg-white/[0.02] opacity-60"}`}>
                      <div className="relative inline-block mb-3">
                        <Icon className="w-8 h-8" style={{ color: b.earned ? "#F5C842" : "#64748b" }} />
                        {!b.earned && <Lock className="w-3.5 h-3.5 text-slate-500 absolute -bottom-1 -right-1" />}
                      </div>
                      <p className="text-sm font-medium text-slate-100">{b.name}</p>
                      <p className="text-xs text-slate-500 mt-1">{b.desc}</p>
                    </div>
                  );
                })}
              </div>
            </>
          ) : <p className="text-slate-500">Loading your journey…</p>}
        </div>
      </div>
    </div>
  );
}
